/**
 * HistoryTimeline.tsx
 *
 * Shared activity-history timeline rendered in both:
 *  - ItemModal (History tab)
 *  - HistoryDrawer (right-side row panel)
 *
 * Exports EVENT_CONFIG so callers can look up colours/icons if needed.
 */

import React from "react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import type { HistoryEvent } from "../hooks/useExpiryItems";

// ── Event colour / icon config ────────────────────────────────────────────────

export const EVENT_CONFIG: Record<
  HistoryEvent["type"],
  { color: string; bg: string; label: string; icon: React.ReactNode }
> = {
  created: {
    color: "text-[#2563EB]",
    bg: "bg-[#EFF6FF]",
    label: "Created",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
    ),
  },
  renewed: {
    color: "text-[#15803D]",
    bg: "bg-[#DCFCE7]",
    label: "Renewed",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
  },
  edited: {
    color: "text-[#6B7280]",
    bg: "bg-[#F3F4F6]",
    label: "Edited",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
  },
  snoozed: {
    color: "text-[#D97706]",
    bg: "bg-[#FEF3C7]",
    label: "Snoozed",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  assigned: {
    color: "text-[#7C3AED]",
    bg: "bg-[#F5F3FF]",
    label: "Assigned",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
  reminder_sent: {
    color: "text-[#0891B2]",
    bg: "bg-[#ECFEFF]",
    label: "Reminder Sent",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  acknowledged: {
    color: "text-[#15803D]",
    bg: "bg-[#F0FDF4]",
    label: "Acknowledged",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
  },
  archived: {
    color: "text-[#6B7280]",
    bg: "bg-[#F3F4F6]",
    label: "Archived",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
        />
      </svg>
    ),
  },
  restored: {
    color: "text-[#7C3AED]",
    bg: "bg-[#F5F3FF]",
    label: "Restored",
    icon: (
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

interface HistoryTimelineProps {
  events: HistoryEvent[];
}

const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ events }) => {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="h-12 w-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-3">
          <svg
            className="h-6 w-6 text-[#9CA3AF]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-[#374151]">No history yet</p>
        <p className="text-xs text-[#9CA3AF] mt-1">
          Events will appear here as changes are made.
        </p>
      </div>
    );
  }

  return (
    <ol className="relative">
      {events.map((event, idx) => {
        const cfg = EVENT_CONFIG[event.type] ?? EVENT_CONFIG.edited;
        const isLast = idx === events.length - 1;
        return (
          <li key={event.id} className="flex gap-3 pb-5">
            {/* Timeline spine */}
            <div className="flex flex-col items-center">
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.bg} ${cfg.color}`}
              >
                {cfg.icon}
              </div>
              {!isLast && <div className="w-px flex-1 bg-[#E5E7EB] mt-1" />}
            </div>

            {/* Event content */}
            <div className="pt-0.5 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}
                >
                  {cfg.label}
                </span>
                <span className="text-xs text-[#9CA3AF]">
                  {formatDistanceToNow(parseISO(event.date), {
                    addSuffix: true,
                  })}
                </span>
                <span className="text-xs text-[#9CA3AF] hidden sm:inline">
                  · {format(parseISO(event.date), "MMM d, yyyy")}
                </span>
              </div>

              <p className="text-sm text-[#374151] mt-1">{event.description}</p>

              {event.user && (
                <p className="text-xs text-[#9CA3AF] mt-0.5">by {event.user}</p>
              )}

              {/* Renewal date diff */}
              {event.type === "renewed" &&
                event.meta?.previousDate &&
                event.meta?.newDate && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-[#F0FDF4] border border-[#86EFAC] rounded-md px-2.5 py-1.5 text-xs">
                    <span className="text-[#DC2626] line-through">
                      {event.meta.previousDate}
                    </span>
                    <svg
                      className="h-3 w-3 text-[#9CA3AF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <span className="text-[#15803D] font-medium">
                      {event.meta.newDate}
                    </span>
                  </div>
                )}

              {/* Field edit diff */}
              {event.type === "edited" && event.meta?.field && (
                <div className="mt-1.5 text-xs text-[#6B7280]">
                  <span className="font-medium capitalize">
                    {event.meta.field}:
                  </span>{" "}
                  <span className="line-through text-[#DC2626]">
                    {event.meta.previousValue}
                  </span>{" "}
                  <span className="text-[#9CA3AF]">→</span>{" "}
                  <span className="text-[#15803D]">{event.meta.newValue}</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default HistoryTimeline;
